import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { useDeleteUser, useSaveUser, useUsersList, useToggleUserStatus } from "../../hooks/useUsers";
import { useDebounce } from "../../hooks/useDebounce";
import { UserRole } from "../../types/user";

import ManageUser from "../../components/users/ManageUser";
import Button from "../../components/ui/button";
import { displayDate } from "../../utils/date";
import Paginate from "../../components/ui/Paginate";
import { DateDataBaseType } from "../../types/database";
import { EditUser, User } from "../../types/user";
import { isSdiAdmin } from "../../utils/roles";
import { RequestError } from "../../errors/RequestError";

import CroixIcon from "../../components/icons/Close";
import UsersIcon from "../../components/icons/Users";
import EditIcon from "../../components/icons/Edit";
import LockIcon from "../../components/icons/Lock";
import DeleteIcon from "../../components/icons/Delete";
import SearchIcon from "../../components/icons/Search";
import { Helmet } from "react-helmet-async";
import useAuth from "../../hooks/useAuth";
import PageSectionHeader from "../../components/PageSectionHeader";

function roleMessageId(role: UserRole): string {
  switch (role) {
    case UserRole.admin:
      return "misc.roles.admin";
    case UserRole.site_admin:
      return "misc.roles.siteAdmin";
    case UserRole.operator:
      return "misc.roles.operator";
    default:
      return "misc.roles.operator";
  }
}

export default function Users () {
  const intl = useIntl();
  const {user: currentUser} = useAuth();
  const [roleFilter, setRoleFilter] = useState<"all" | UserRole>("all");
  
  const {fetchUsers, search, metaData, users, isLoading} = useUsersList({ 
    role: roleFilter 
  });
  const {saveUser, saved, error: errorWhenSaving} = useSaveUser();
  const {deleteUser, response: deleted} = useDeleteUser();
  const {toggleUserStatus, response: statusToggled} = useToggleUserStatus();
  
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User|null>(null);
  const [error, setError] = useState<Error | RequestError | null>(null);
  const [success, setSuccess] = useState<{message: string} | null>(null);

  const [searchInput, setSearchInput] = useState<string>();
  const termToSearch = useDebounce(searchInput, 700);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers])

  useEffect(() => {
    setError(errorWhenSaving);
  }, [errorWhenSaving])

  useEffect(() => {
    if (deleted) {
      termToSearch ? search(termToSearch, metaData.page) : fetchUsers();
      closeModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted, fetchUsers, search, termToSearch])

  useEffect(() => {
    if (termToSearch) {
      search(termToSearch);
    } else {
      fetchUsers();
    }
  }, [fetchUsers, search, termToSearch, roleFilter]);

  useEffect(() => {
    if (saved) {
      fetchUsers();
      setSuccess({
        message: saved.message || intl.formatMessage({ id: "users.saveSuccess" })
      });
      if (!userToEdit) {
        setUserToEdit(saved.user);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers, saved, intl])

  useEffect(() => {
    if (statusToggled) {
      fetchUsers();
      setSuccess({ message: intl.formatMessage({ id: "users.statusToggled" }) });
      setTimeout(() => setSuccess(null), 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusToggled, fetchUsers, intl])

  function onUserEdit(user: User & DateDataBaseType): void {
    if (currentUser?.role === UserRole.site_admin && user.role !== UserRole.operator) {
      return;
    }
    setShowModal(true);
    setUserToEdit(user);
  }

  function closeModal() {
    setShowModal(false);
    setUserToEdit(null);
    setError(null);
    setSuccess(null);
  }

  const onSave = useCallback((action: "ADD" | "UPDATE", user: EditUser) => {
    if (action === "UPDATE" && userToEdit) {
      saveUser(action, user, userToEdit.id);
    } else {
      saveUser(action, user);
    }
  }, [saveUser, userToEdit])

  const onDelete = useCallback(() => {setDeleteModal(true)}, [])

  const onToggleStatus = useCallback((user: User & DateDataBaseType) => {
    if (user.id) {
      toggleUserStatus(user.id, !user.active);
    }
  }, [toggleUserStatus])

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "users.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>
      <div className="h-full flex flex-col">
        {/* Success/Error Messages */}
        {(success || error) && (
          <div className={`mb-4 p-4 rounded-lg ${success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm font-medium ${success ? 'text-green-800' : 'text-red-800'}`}>
              {success
                ? success.message
                : error?.message || intl.formatMessage({ id: "common.errorGeneric" })}
            </p>
          </div>
        )}

        {/* Header */}
        <PageSectionHeader>
          <h2 className="text-2xl font-semibold" style={{ color: '#023865' }}>
            {intl.formatMessage(
              { id: "common.welcomeUser" },
              { firstName: currentUser?.firstName, lastName: currentUser?.lastName }
            )}
          </h2>
        </PageSectionHeader>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-1">
            <div className="relative flex-1 max-w-md">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input 
                type="search"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={intl.formatMessage({ id: "users.searchPlaceholder" })}
                value={searchInput || ''}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            
            <select 
              className="pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as "all" | UserRole)}
            >
              <option value="all">{intl.formatMessage({ id: "users.roleFilter" })}</option>
              {isSdiAdmin(currentUser?.role) && (
                <option value={UserRole.admin}>{intl.formatMessage({ id: "users.roleAdminSdi" })}</option>
              )}
              {isSdiAdmin(currentUser?.role) && (
                <option value={UserRole.site_admin}>{intl.formatMessage({ id: "users.roleSiteAdmin" })}</option>
              )}
              <option value={UserRole.operator}>{intl.formatMessage({ id: "users.roleOperator" })}</option>
            </select>
          </div>

          <button 
            className="px-4 py-3 flex items-center gap-2 text-white rounded-lg shrink-0 whitespace-nowrap" 
            type="button" 
            style={{ backgroundColor: '#E94E1B' }}
            onClick={() => setShowModal(true)}
          >
            <span className="text-xl">+</span>
            {intl.formatMessage({ id: "users.createUser" })}
          </button>
        </div>

        {/* Users List Section */}
        <section className="bg-white rounded-2xl p-6" style={{ border: '1px solid #E4E7EC' }}>
          <div className="mb-6 flex justify-between items-center">
            <div>
              <UsersIcon className="w-6 h-6 text-gray-700 mb-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "users.listTitle" })}
              </h3>
            </div>
            <span className="text-sm text-gray-600">
              {intl.formatMessage(
                { id: "users.userCount" },
                { count: metaData.totalItems }
              )}
            </span>
          </div>
          
          <div className="overflow-x-auto">
            { isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <p className="mt-2 text-gray-500">{intl.formatMessage({ id: "common.loading" })}</p>
              </div>
            ) : users && users.length > 0 ? (
              <table className="w-full border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '25%' }}>
                      {intl.formatMessage({ id: "users.colName" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '30%' }}>
                      {intl.formatMessage({ id: "users.colEmail" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '20%' }}>
                      {intl.formatMessage({ id: "users.colRole" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '15%' }}>
                      {intl.formatMessage({ id: "users.colSignup" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '10%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-4 py-4 text-left text-sm font-medium text-gray-900 rounded-l-2xl" style={{ border: '1px solid #E4E7EC', borderRight: 'none' }}>
                        <div className="flex items-center gap-2">
                          {user.firstName} {user.lastName}                          
                        </div>
                      </td>
                      <td className="px-4 py-4 text-left text-sm font-medium text-orange-500" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                        {user.email}
                      </td>
                      <td className="px-4 py-4 text-left text-sm text-gray-700" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                        <span>{intl.formatMessage({ id: roleMessageId(user.role) })}</span>
                      </td>
                      <td className="px-4 py-4 text-left text-sm" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                        <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium" style={{ backgroundColor: '#F9FAFB', color: '#9CA3AF' }}>
                          {displayDate(user.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-left rounded-r-2xl" style={{ border: '1px solid #E4E7EC', borderLeft: 'none' }}>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const cannotEdit = currentUser?.role === UserRole.site_admin && user.role !== UserRole.operator;
                            return (
                          <button
                            className={`transition-colors ${cannotEdit ? 'opacity-40 cursor-not-allowed' : ''}`}
                            style={{ color: '#023865' }}
                            onClick={() => onUserEdit(user)}
                            title={
                              cannotEdit
                                ? intl.formatMessage({ id: "users.cannotEditOnlyOperators" })
                                : intl.formatMessage({ id: "users.editTitle" })
                            }
                            disabled={cannotEdit}
                          >
                            <EditIcon className="w-5 h-5" />
                          </button>
                            );
                          })()}
                          <button
                            className="transition-colors"
                            style={{ color: '#023865' }}
                            onClick={() => onToggleStatus(user)}
                            title={
                              user.active
                                ? intl.formatMessage({ id: "users.deactivateUser" })
                                : intl.formatMessage({ id: "users.activateUser" })
                            }
                          >
                            <LockIcon className="w-5 h-5" />
                          </button>
                          <button
                            className="transition-colors"
                            style={{ color: '#E94E1B' }}
                            onClick={() => {
                              setUserToEdit(user);
                              setDeleteModal(true);
                            }}
                            title={intl.formatMessage({ id: "users.deleteTitle" })}
                          >
                            <DeleteIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {intl.formatMessage({ id: "users.empty" })}
              </div>
            )}
          </div>

          {/* Pagination */}
          { metaData.totalPages > 1 &&
            <div className="mt-6 flex justify-center">
              <Paginate {...metaData}
                to={page => termToSearch ? search(termToSearch, page) : fetchUsers(page)}/>
            </div>
          }
        </section>

      </div>

      {/* Modal edit/add */}
      <div className={`py-5 md:py-10 fixed top-0 right-0 bottom-0 w-full h-full grid place-items-center transition-colors z-50
        ${showModal ? "bg-black/35 pointer-events-auto overflow-y-auto" : "pointer-events-none"}`}
        data-overlay
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(e: any) => e.target.dataset?.overlay && closeModal()}>
        <div className={`md:ml-auto pt-10 pb-10 px-8 md:w-1/2 lg:w-1/3 2xl:w-1/4 bg-white rounded-lg relative shadow-xl
          transition-transform ${showModal ? 'mx-5 md:mr-8 lg:mr-10 translate-x-0' : 'translate-x-full'}`}>
          <button onClick={() => closeModal()} className="p-1.5 absolute top-5 right-5"><CroixIcon /></button>
          <ManageUser
            key={userToEdit && userToEdit.id || 0}
            error={error}
            success={success}
            user={userToEdit}
            onSave={onSave}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Modal delete user */}
      {
        deleteModal &&
        <div className="fixed top-0 right-0 w-full h-full grid place-items-center bg-black/35 z-50">
          <div className="px-8 py-6 w-3/4 md:w-2/4 lg:w-2/5 2xl:w-1/4 rounded-lg bg-white shadow-2xl">
            <h3 className="text-center text-gray-900 font-semibold text-xl">
              {intl.formatMessage({ id: "users.deleteConfirmTitle" })} <br />
              <strong className="font-bold">{userToEdit?.firstName + ' ' + userToEdit?.lastName}</strong>
            </h3>
            <p className="text-sm font-medium mt-3 text-center text-gray-600">
              {intl.formatMessage({ id: "users.deleteConfirmBody" })}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button theme="black" size="small" onClick={() => setDeleteModal(false)}>
                {intl.formatMessage({ id: "common.cancel" })}
              </Button>
              <Button
                theme="danger" size="small"
                onClick={() => {
                  userToEdit?.id && deleteUser(userToEdit.id);
                  setDeleteModal(false);
                }
                }>
                {intl.formatMessage({ id: "common.delete" })}
              </Button>
            </div>
          </div>
        </div>
      }
    </>
  )
}
